/**
 * BrokerPortalHub — standalone navy+gold hub for broker operational workflow.
 *
 * Distinct from BrokerIntelligenceHub (admin/oversight). This hub is where
 * brokers review documents, approve transactions, manage signing, and track
 * compliance on day-to-day operations.
 *
 * Route: /broker-portal/*
 * Reuses HubShell + shared components from broker-intelligence.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  faFileContract,
  faClipboardCheck,
  faFolderOpen,
  faShieldAlt,
  faChartLine,
  faEye,
  faColumns,
  faHeartbeat,
} from '@fortawesome/free-solid-svg-icons';
import { HubShell, OutlineButton } from '../broker-intelligence/shared';
import type { HubTab, HubMetric } from '../broker-intelligence/shared';
import { colors } from '../broker-intelligence/shared/colors';
import axiosInstance from '../../helpers/api';

// Existing working components
import TransactionManagement from '../../components/broker-portal/TransactionManagement';
import DocumentReviewInterface from '../../components/broker-portal/DocumentReviewInterface';
import TemplateManager from '../../components/broker-portal/TemplateManagerBootstrap';
import ComplianceDashboard from '../../components/broker-portal/ComplianceDashboard';
import TransactionDetails from '../../components/agent-portal/TransactionDetails';

import BrokerageDealBoard from '../../components/broker-portal/BrokerageDealBoard';
import DealHealthDashboard from '../../components/broker-portal/DealHealthDashboard';

import { useAuth } from '../../hooks/useAuth';
import AISupervisionTab from './AISupervisonTab';

// ─── Tab Definitions ─────────────────────────────────────────────────

const TABS: HubTab[] = [
  { key: 'deal-board',      label: 'Deal Board',      icon: faColumns },
  { key: 'deal-health',     label: 'Deal Health',     icon: faHeartbeat },
  { key: 'transactions',    label: 'Transactions',    icon: faFileContract },
  { key: 'document-review', label: 'Document Review', icon: faClipboardCheck },
  { key: 'templates',       label: 'Templates',       icon: faFolderOpen },
  { key: 'compliance',      label: 'Compliance',      icon: faShieldAlt },
  { key: 'ai-supervision',  label: 'AI Supervision',  icon: faEye },
];

// ─── Helpers ─────────────────────────────────────────────────────────

function getTabFromPath(pathname: string): string {
  const segments = pathname.replace(/\/$/, '').split('/');
  const afterPortal = segments[segments.indexOf('broker-portal') + 1];
  // /broker-portal/transactions/:id should still show transactions tab
  if (afterPortal === 'transactions') return 'transactions';
  if (afterPortal && TABS.some(t => t.key === afterPortal)) return afterPortal;
  return 'deal-board';
}

function getTransactionIdFromPath(pathname: string): string | null {
  const match = pathname.match(/\/broker-portal\/transactions\/([^/]+)/);
  return match ? match[1] : null;
}

function getCurrentUser(): any {
  const merged: any = {};
  try {
    const stored = localStorage.getItem('user');
    if (stored) Object.assign(merged, JSON.parse(stored));
  } catch { /* ignore */ }
  try {
    const stored = localStorage.getItem('user_info');
    if (stored) Object.assign(merged, JSON.parse(stored));
  } catch { /* ignore */ }
  try {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.role && !merged.role) merged.role = payload.role;
      if (payload.email && !merged.email) merged.email = payload.email;
      if (payload.id && !merged.id) merged.id = payload.id;
    }
  } catch { /* ignore */ }
  return Object.keys(merged).length > 0 ? merged : null;
}

// ─── Component ───────────────────────────────────────────────────────

const BrokerPortalHub: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const transactionId = getTransactionIdFromPath(location.pathname);
  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));
  const { user } = useAuth();
  const [stats, setStats] = useState<any>({});
  const [sponsorType, setSponsorType] = useState<string | null>(null);

  const currentUser = useMemo(() => getCurrentUser(), []);

  // Sync tab from URL
  useEffect(() => {
    const tab = getTabFromPath(location.pathname);
    if (tab !== activeTab) setActiveTab(tab);
  }, [location.pathname]);

  // Fetch portal stats + brokerage info
  useEffect(() => {
    axiosInstance.get('/broker-portal/stats')
      .then(r => setStats(r.data?.stats || {}))
      .catch(() => {});

    axiosInstance.get('/broker/company-profile')
      .then(r => {
        const data = r.data?.data;
        if (data?.sponsor_type) setSponsorType(data.sponsor_type);
      })
      .catch(() => {});
  }, []);

  const handleTabChange = useCallback((key: string) => {
    setActiveTab(key);
    navigate(`/broker-portal/${key}`, { replace: true });
  }, [navigate]);

  // Metrics strip
  const metrics: HubMetric[] = useMemo(() => [
    { icon: faFileContract, value: stats.total_transactions || 0, label: 'Active Transactions', color: colors.gold2 },
    { icon: faClipboardCheck, value: stats.pending_review || 0, label: 'Pending Review', color: colors.orange },
    { icon: faShieldAlt, value: stats.approved_documents || 0, label: 'Approved', color: colors.green },
    { icon: faChartLine, value: stats.total_documents || 0, label: 'Total Documents', color: colors.blue },
  ], [stats]);

  // Portal label based on sponsor type
  const portalLabel = sponsorType === 'lender' ? 'Lending'
    : sponsorType === 'title' ? 'Title'
    : 'Broker';

  // Breadcrumb
  const currentTab = TABS.find(t => t.key === activeTab);
  const breadcrumb = `${portalLabel} Portal / ${currentTab?.label || 'Transactions'}`;

  // Access check
  if (!currentUser) {
    return (
      <div style={{ background: colors.navy, height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.white }}>
        <div style={{ textAlign: 'center' }}>
          <h2>Access Denied</h2>
          <p style={{ color: colors.muted }}>Please log in to access the Broker Portal.</p>
        </div>
      </div>
    );
  }

  const actions = (
    <>
      <OutlineButton onClick={() => navigate('/broker-portal/compliance-hub')}>Document Compliance</OutlineButton>
      <OutlineButton onClick={() => navigate('/broker-intelligence')}>Intelligence</OutlineButton>
    </>
  );

  return (
    <HubShell
      breadcrumb={breadcrumb}
      brandMiddle={portalLabel}
      metrics={metrics}
      actions={actions}
      tabs={TABS}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      homeHref="/agent-portal/dashboard"
    >
      {/* Transaction detail view — renders when URL has transactionId, from any tab */}
      {transactionId && (
        <div style={{ background: '#fff', padding: 16, overflowY: 'auto', height: '100%' }}>
          <TransactionDetails
            transactionId={transactionId}
            onBack={() => navigate('/broker-portal/deal-board')}
            portal="broker"
          />
        </div>
      )}
      {activeTab === 'deal-board' && !transactionId && (
        <div style={{ background: '#fff', padding: 16, overflowY: 'auto', height: '100%' }}>
          <BrokerageDealBoard />
        </div>
      )}
      {activeTab === 'deal-health' && !transactionId && (
        <div style={{ background: '#fff', padding: 16, overflowY: 'auto', height: '100%' }}>
          <DealHealthDashboard />
        </div>
      )}
      {activeTab === 'transactions' && !transactionId && (
        <div style={{ background: '#fff', padding: 16, overflowY: 'auto', height: '100%' }}>
          <TransactionManagement />
        </div>
      )}
      {activeTab === 'document-review' && (
        <div style={{ background: '#fff', padding: 16, overflowY: 'auto', height: '100%' }}>
          <DocumentReviewInterface
            brokerId={user?.id}
            onDocumentReviewed={(docId: string, status: string) => {
              console.log(`Document ${docId} reviewed: ${status}`);
              axiosInstance.get('/broker-portal/stats')
                .then(r => setStats(r.data?.stats || {}))
                .catch(() => {});
            }}
          />
        </div>
      )}
      {activeTab === 'templates' && (
        <div style={{ background: '#fff', padding: 16, overflowY: 'auto', height: '100%' }}>
          <TemplateManager />
        </div>
      )}
      {activeTab === 'compliance' && (
        <div style={{ background: '#fff', padding: 16, overflowY: 'auto', height: '100%' }}>
          <ComplianceDashboard />
        </div>
      )}
      {activeTab === 'ai-supervision' && (
        <AISupervisionTab />
      )}
    </HubShell>
  );
};

export default BrokerPortalHub;
